import React, { useState, useEffect } from 'react';
import { Plus, Settings, Trash2, RefreshCw, Check, X, AlertCircle, Zap, Activity } from 'lucide-react';
import integrationService, {
  Integration,
  IntegrationType,
  IntegrationType_Definition,
  CreateIntegrationInput,
  getIntegrationIcon,
  getIntegrationStatusColor,
  getIntegrationStatusText,
  formatRelativeTime,
} from '../../services/integrationService';
import IntegrationLogs from './IntegrationLogs';

const Integrations: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [availableTypes, setAvailableTypes] = useState<IntegrationType_Definition[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'marketplace' | 'my-integrations'>('my-integrations');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [selectedType, setSelectedType] = useState<IntegrationType_Definition | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [integrationsData, typesData] = await Promise.all([
        integrationService.getIntegrations(),
        integrationService.getAvailableTypes(),
      ]);
      setIntegrations(integrationsData);
      setAvailableTypes(typesData);
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = (type: IntegrationType_Definition) => {
    if (type.comingSoon) {
      alert('This integration is coming soon!');
      return;
    }
    setSelectedType(type);
    setShowSetupModal(true);
  };

  const handleEdit = (integration: Integration) => {
    const typeDef = availableTypes.find(t => t.type === integration.type);
    if (typeDef) {
      setSelectedIntegration(integration);
      setSelectedType(typeDef);
      setShowSetupModal(true);
    }
  };

  const handleDelete = async (integration: Integration) => {
    if (!confirm(`Delete integration "${integration.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await integrationService.deleteIntegration(integration.id);
      await loadData();
    } catch (error) {
      console.error('Failed to delete integration:', error);
      alert('Failed to delete integration');
    }
  };

  const handleTest = async (integration: Integration) => {
    try {
      await integrationService.testIntegration(integration.id);
      alert('Connection test successful!');
      await loadData();
    } catch (error: any) {
      alert(`Connection test failed: ${error.message}`);
    }
  };

  const handleToggleActive = async (integration: Integration) => {
    try {
      await integrationService.updateIntegration(integration.id, {
        isActive: !integration.isActive,
      });
      await loadData();
    } catch (error) {
      console.error('Failed to toggle integration:', error);
      alert('Failed to update integration');
    }
  };

  const handleViewLogs = (integration: Integration) => {
    setSelectedIntegration(integration);
    setShowLogsModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Integrations</h1>
          <p className="mt-2 text-gray-600">
            Connect your account with third-party services
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('my-integrations')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                view === 'my-integrations'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Integrations
            </button>
            <button
              onClick={() => setView('marketplace')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                view === 'marketplace'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Marketplace
            </button>
          </div>
        </div>
      </div>

      {/* Marketplace View */}
      {view === 'marketplace' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {availableTypes.map((type) => {
            const connectedCount = integrations.filter(i => i.type === type.type).length;
            return (
              <div key={type.type} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">{getIntegrationIcon(type.type as IntegrationType)}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{type.name}</h3>
                      {connectedCount > 0 && (
                        <span className="text-xs text-green-600">{connectedCount} connected</span>
                      )}
                    </div>
                  </div>
                  {type.comingSoon && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-4">{type.description}</p>
                <button
                  onClick={() => handleSetup(type)}
                  disabled={type.comingSoon}
                  className={`w-full px-4 py-2 rounded-lg transition ${
                    type.comingSoon
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Plus size={16} />
                    {type.comingSoon ? 'Coming Soon' : 'Connect'}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* My Integrations View */}
      {view === 'my-integrations' && (
        <>
          {integrations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Zap size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Integrations</h3>
              <p className="text-gray-600 mb-4">
                Connect third-party services to automate your workflow
              </p>
              <button
                onClick={() => setView('marketplace')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                Browse Marketplace
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {integrations.map((integration) => {
                const statusColor = getIntegrationStatusColor(integration);
                const statusText = getIntegrationStatusText(integration);
                
                return (
                  <div key={integration.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-3xl">{getIntegrationIcon(integration.type as IntegrationType)}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-semibold text-gray-900">{integration.name}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${statusColor}`}>
                              {statusText}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 capitalize">{integration.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewLogs(integration)}
                          className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                          title="View Logs"
                        >
                          <Activity size={18} />
                        </button>
                        <button
                          onClick={() => handleEdit(integration)}
                          className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                          title="Settings"
                        >
                          <Settings size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(integration)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Error Message */}
                    {integration.lastError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-800">Last Error</p>
                          <p className="text-sm text-red-700">{integration.lastError}</p>
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-sm text-gray-600">Status</div>
                        <div className="text-sm font-medium text-gray-900">
                          {integration.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Last Sync</div>
                        <div className="text-sm font-medium text-gray-900">
                          {integration.lastSyncAt ? formatRelativeTime(integration.lastSyncAt) : 'Never'}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Total Logs</div>
                        <div className="text-sm font-medium text-gray-900">
                          {integration._count?.logs || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Created</div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatRelativeTime(integration.createdAt)}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-4 border-t">
                      <button
                        onClick={() => handleTest(integration)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      >
                        <RefreshCw size={16} />
                        Test Connection
                      </button>
                      <button
                        onClick={() => handleToggleActive(integration)}
                        className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition ${
                          integration.isActive
                            ? 'text-gray-600 hover:bg-gray-100'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {integration.isActive ? (
                          <>
                            <X size={16} />
                            Disable
                          </>
                        ) : (
                          <>
                            <Check size={16} />
                            Enable
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Setup/Edit Modal */}
      {showSetupModal && selectedType && (
        <IntegrationSetupModal
          type={selectedType}
          integration={selectedIntegration}
          onClose={() => {
            setShowSetupModal(false);
            setSelectedType(null);
            setSelectedIntegration(null);
          }}
          onSuccess={() => {
            setShowSetupModal(false);
            setSelectedType(null);
            setSelectedIntegration(null);
            loadData();
          }}
        />
      )}

      {/* Logs Modal */}
      {showLogsModal && selectedIntegration && (
        <IntegrationLogsModal
          integration={selectedIntegration}
          onClose={() => {
            setShowLogsModal(false);
            setSelectedIntegration(null);
          }}
        />
      )}
    </div>
  );
};

// Integration Setup Modal Component
const IntegrationSetupModal: React.FC<{
  type: IntegrationType_Definition;
  integration: Integration | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ type, integration, onClose, onSuccess }) => {
  const isEditing = !!integration;
  
  const [formData, setFormData] = useState<CreateIntegrationInput>({
    name: integration?.name || `My ${type.name} Integration`,
    type: type.type as IntegrationType,
    config: integration?.config || {},
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (type.fields) {
        for (const field of type.fields) {
          if (field.required && !formData.config[field.name]) {
            setError(`${field.label} is required`);
            setLoading(false);
            return;
          }
        }
      }

      if (isEditing) {
        await integrationService.updateIntegration(integration.id, {
          name: formData.name,
          config: formData.config,
        });
      } else {
        await integrationService.createIntegration(formData);
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} integration`);
      setLoading(false);
    }
  };

  const handleConfigChange = (fieldName: string, value: string) => {
    setFormData({
      ...formData,
      config: {
        ...formData.config,
        [fieldName]: value,
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="text-3xl">{getIntegrationIcon(type.type as IntegrationType)}</div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit' : 'Setup'} {type.name}
              </h2>
              <p className="text-gray-600 text-sm">{type.description}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Integration Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder={`My ${type.name} Integration`}
                required
              />
            </div>

            {/* Dynamic Fields */}
            {type.fields && type.fields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label} {field.required && '*'}
                </label>
                <input
                  type={field.type}
                  value={formData.config[field.name] || ''}
                  onChange={(e) => handleConfigChange(field.name, e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder={field.placeholder}
                  required={field.required}
                />
              </div>
            ))}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
              >
                {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Integration Logs Modal Component
const IntegrationLogsModal: React.FC<{
  integration: Integration;
  onClose: () => void;
}> = ({ integration, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="text-3xl">{getIntegrationIcon(integration.type as IntegrationType)}</div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{integration.name}</h2>
                <p className="text-gray-600 text-sm">Integration Logs</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition"
            >
              <X size={24} />
            </button>
          </div>

          <IntegrationLogs integrationId={integration.id} />
        </div>
      </div>
    </div>
  );
};

export default Integrations;
